import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AgencyBillingSummary } from "@/types/billing";
import {
  DollarSign,
  Users,
  TrendingUp,
  AlertTriangle,
  Clock,
  CreditCard
} from "lucide-react";

interface BillingSummaryCardsProps {
  summary: AgencyBillingSummary | null;
  isLoading?: boolean;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export function BillingSummaryCards({ summary, isLoading }: BillingSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20" />
              <Skeleton className="h-3 w-32 mt-1" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Default values when no summary data
  const data = summary || {
    total_clients: 0,
    total_base_cost: 0,
    total_markup: 0,
    total_billed: 0,
    unbilled_amount: 0,
    overdue_amount: 0,
    period_start: "",
    period_end: "",
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Total Billed</CardTitle>
          <DollarSign className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(data.total_billed)}</div>
          <p className="text-xs text-muted-foreground">
            {formatCurrency(data.total_base_cost)} base + {formatCurrency(data.total_markup)} markup
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Clients</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{data.total_clients}</div>
          <p className="text-xs text-muted-foreground">
            Generating usage this period
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Markup Revenue</CardTitle>
          <TrendingUp className="h-4 w-4 text-green-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(data.total_markup)}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.total_base_cost > 0
              ? `${((data.total_markup / data.total_base_cost) * 100).toFixed(1)}% average markup`
              : "No usage yet"}
          </p>
        </CardContent>
      </Card>

      <Card className={data.unbilled_amount > 0 ? "border-amber-200" : ""}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Unbilled Amount</CardTitle>
          <Clock className="h-4 w-4 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${data.unbilled_amount > 0 ? "text-amber-600" : ""}`}>
            {formatCurrency(data.unbilled_amount)}
          </div>
          <p className="text-xs text-muted-foreground">
            {data.unbilled_amount > 0 ? "Ready for invoicing" : "All usage invoiced"}
          </p>
        </CardContent>
      </Card>

      {data.overdue_amount > 0 && (
        <Card className="border-red-200 md:col-span-2 lg:col-span-4">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Overdue Invoices
            </CardTitle>
            <CreditCard className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {formatCurrency(data.overdue_amount)}
            </div>
            <p className="text-xs text-muted-foreground">
              Requires attention - follow up with clients
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
